export const CHECKOUT_URL = 'https://glpdecision-en.impultienda.ar/checkout/'

export const HERO_MOCKUP =
  'https://cdn.magicpatterns.com/uploads/749gjPsYCk79SiDYmaATiE/GLPDECISION-EN.png'

export const DOCTOR_PHOTO =
  'https://cdn.magicpatterns.com/uploads/cvUv8RqKB1xXDf3pBkJfa2/Dra_Karito_real.png'

export const stats = [
  { value: '+300', label: 'Decision maps, checklists & clinical tools' },
  { value: '+1,200', label: "Physicians using Dr. Karito's systems" },
  { value: '10 min', label: 'To organize your consultation' },
  { value: '6', label: 'Premium clinical volumes' },
]

export const beforeItems = [
  { icon: '😰', text: 'You search through different guidelines before a GLP-1 consultation.' },
  { icon: '⏱️', text: 'You waste time trying to remember what information you should review.' },
  {
    icon: '🤷',
    text: "You know about GLP-1 therapy, but you're unsure how to structure the information in front of the patient.",
  },
  { icon: '📚', text: 'You jump between articles, PDFs, notes, and clinical resources.' },
  { icon: '😓', text: 'Every patient seems to require a completely different approach.' },
  {
    icon: '😩',
    text: 'Before a complex consultation, you feel like you need to "study everything again."',
  },
]

export const afterItems = [
  { icon: '🗺️', text: 'Follow a visual map that structures each stage of the consultation.' },
  { icon: '🔎', text: 'Know what information needs to be reviewed or verified before moving forward.' },
  { icon: '📋', text: 'Use professional checklists instead of relying entirely on memory.' },
  { icon: '🧭', text: 'Follow a consistent consultation sequence.' },
  { icon: '📊', text: 'Identify what information you have and what information is still missing.' },
  { icon: '⚡', text: 'Organize the structure of your consultation in under 10 minutes.' },
]

export const painPoints = [
  {
    icon: '😰',
    text: "You have a patient interested in GLP-1 therapy and you're not completely sure what information you should review first.",
  },
  { icon: '⏱️', text: 'You spend too much time jumping between different sources before a consultation.' },
  {
    icon: '🤷',
    text: 'You understand GLP-1 therapy, but struggle to turn that knowledge into a structured consultation process.',
  },
  {
    icon: '📚',
    text: "You have saved guidelines, articles, PDFs, and notes—but when you need them, you don't know exactly where to start.",
  },
  {
    icon: '😓',
    text: "Your consultations don't always follow the same structure and you want a more standardized process.",
  },
  {
    icon: '😩',
    text: 'Before a complex consultation, you feel like you need to "go back and study everything."',
  },
]

export type Volume = {
  number: string
  title: string
  tagline: string
  description: string
  includes: string[]
}

export const volumes: Volume[] = [
  {
    number: 'VOLUME I',
    title: 'The GLP-1 Consultation Map',
    tagline: 'Stop asking yourself, "Where do I start?"',
    description:
      'Learn how to structure a GLP-1 consultation from the first minute using a visual map that breaks the process into clear stages.',
    includes: [
      'Master Consultation Map',
      'Consultation workflow',
      'Preparation checklist',
      'Information-gathering framework',
      'Consultation question map',
      'Closing checklist',
      'Quick preparation worksheet',
    ],
  },
  {
    number: 'VOLUME II',
    title: 'The Patient Assessment Map',
    tagline: 'Organize the information you need to review before moving forward',
    description:
      'A visual framework for organizing relevant history, medications, clinical information, risk considerations, and information that requires further verification.',
    includes: [
      'Patient Assessment Map',
      'Information checklist',
      'Medication Review Map',
      'Baseline Information Checklist',
      'Missing Information Tracker',
      'Patient Profile Worksheet',
    ],
  },
  {
    number: 'VOLUME III',
    title: 'The Clinical Decision Map',
    tagline: 'Turn scattered information into a structured reasoning process',
    description:
      'A visual system for identifying which factors need to be considered, what information should be verified, and when additional review is necessary.',
    includes: [
      'Clinical Decision Map',
      'Clinical Question Framework',
      'Evidence Verification Checklist',
      'Decision Documentation Sheet',
      'Patient-Specific Factors Map',
      'Clinical Reasoning Worksheet',
    ],
  },
  {
    number: 'VOLUME IV',
    title: 'The Monitoring & Follow-Up Map',
    tagline: 'Stop improvising your follow-up process',
    description:
      'Organize the information you need to review from one consultation to the next.',
    includes: [
      'Follow-Up Map',
      'Monitoring Checklist',
      'Progress Review Sheet',
      'Patient Concerns Tracker',
      'Adherence Discussion Framework',
      'Follow-Up Documentation Checklist',
    ],
  },
  {
    number: 'VOLUME V',
    title: 'The Complex Case Map',
    tagline: "When a case doesn't fit the standard pathway, use a structured process",
    description:
      'A visual system for organizing cases that require additional information, deeper evidence review, or consideration of referral.',
    includes: [
      'Complex Case Map',
      'Missing Information Checklist',
      'Red-Flag Review Framework',
      'Evidence Verification Workflow',
      'Referral Consideration Checklist',
      'Case Review Worksheet',
    ],
  },
  {
    number: 'VOLUME VI',
    title: 'The Clinical Documentation Map',
    tagline: 'Turn every consultation into a structured, repeatable process',
    description:
      'Organize what to document, how to structure your notes, and what to review before closing the consultation.',
    includes: [
      'Documentation Map',
      'Consultation Documentation Checklist',
      'Decision Rationale Worksheet',
      'Patient Education Documentation',
      'Follow-Up Plan',
      'Consultation Close-Out Checklist',
    ],
  },
]

export const immediateAccess = [
  {
    icon: '🗺️',
    title: '30+ Visual Maps, Checklists & Consultation Tools',
    text: 'Designed to help you structure the key stages of a GLP-1 consultation.',
  },
  {
    icon: '📋',
    title: 'Professional Consultation Checklists',
    text: 'Reduce your reliance on memory and create a more consistent consultation workflow.',
  },
  {
    icon: '🔎',
    title: 'Evidence Verification Frameworks',
    text: 'Identify what information needs to be checked before making a clinical decision.',
  },
  {
    icon: '📊',
    title: 'Patient Assessment Matrices',
    text: 'Organize available information, identify missing data, and structure your review.',
  },
  {
    icon: '⚡',
    title: 'Quick Reference Maps',
    text: 'Condensed visual tools you can use during consultation preparation.',
  },
]

export type Bonus = {
  label: string
  title: string
  value: string
  description: string
  includes?: string[]
  image: string
}

export const bonuses: Bonus[] = [
  {
    label: 'BONUS #1',
    title: '50 GLP-1 Clinical Decision Cases',
    value: '$47',
    description:
      '50 educational scenarios designed to help you practice organizing information from different patient profiles and identifying what should be reviewed or verified before moving forward.',
    includes: [
      'Clinical scenario',
      'Available information',
      'Missing information',
      'Review questions',
      'Reasoning framework',
      'Analysis checklist',
    ],
    image: 'https://cdn.magicpatterns.com/uploads/2EQitJ4TWeHVtWL6wvkhVN/BONUS_1_GLPDECISION-EN.png',
  },
  {
    label: 'BONUS #2',
    title: 'The 10-Minute GLP-1 Consultation Map™',
    value: '$37',
    description:
      'A streamlined version of the system designed to help you organize the structure of a GLP-1 consultation in under 10 minutes.',
    includes: [
      'Rapid preparation checklist',
      'Information review',
      'Consultation map',
      'Key questions',
      'Consultation close-out',
    ],
    image: 'https://cdn.magicpatterns.com/uploads/vHPvZKw3hY7T2bYQLrUtdb/BONUS_2_GLPDECISIONE-N.png',
  },
  {
    label: 'BONUS #3',
    title: 'Clinical Organization Mistakes Library',
    value: '$47',
    description:
      'A collection of common consultation and documentation organization mistakes. Identify what can easily be overlooked and use structured checklists to reduce unnecessary omissions.',
    image: 'https://cdn.magicpatterns.com/uploads/8jga6wwAar2mJGQWQeqaiN/BONUS_3_GLPDECISION-EN.png',
  },
  {
    label: 'BONUS #4',
    title: 'Patient Follow-Up Map™',
    value: '$37',
    description: 'A visual system for organizing follow-up consultations.',
    includes: [
      'Follow-up dashboard',
      'Monitoring checklist',
      'Progress review',
      'Patient concerns',
      'Documentation',
      'Next-visit preparation',
    ],
    image: 'https://cdn.magicpatterns.com/uploads/deiVZuEKoWpGabpUd9it2a/BONUS_4_GLPDECISION-EN.png',
  },
  {
    label: 'BONUS #5',
    title: '30 GLP-1 Quick Reference Cards™',
    value: '$29',
    description: '30 visual reference cards ready to print or save to your tablet. Includes quick-reference tools for:',
    includes: [
      'Initial consultation',
      'Patient assessment',
      'Documentation',
      'Follow-up',
      'Complex cases',
      'Evidence verification',
    ],
    image: 'https://cdn.magicpatterns.com/uploads/38NoAKEu5s8MfVa5o11f87/BONUS_5_GLPDECISION-EN.png',
  },
]

export type Testimonial = {
  name: string
  flag: string
  country: string
  quote: string
  photo: string
}

export const testimonials: Testimonial[] = [
  {
    name: 'Maria L.',
    flag: '🇨🇴',
    country: 'Colombia',
    quote:
      'Before a GLP-1 consultation, I used to jump between several different sources. Now I can quickly organize what information I need to review and where to start.',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/4f33b259-3283-4f1b-a282-a14e3d7bdb01.jpg',
  },
  {
    name: 'José R.',
    flag: '🇲🇽',
    country: 'Mexico',
    quote: 'The consultation map helped me organize a process that previously felt completely scattered.',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/ef677f1c-ba97-4e74-973d-4c16c92d3ed5.jpg',
  },
  {
    name: 'Andrea P.',
    flag: '🇪🇸',
    country: 'Spain',
    quote:
      "What I liked most is that it's not another book. These are tools I can actually open and use when I need them.",
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/9866b655-c685-44ce-964f-59bfa82d7ec3.jpg',
  },
  {
    name: 'Luis M.',
    flag: '🇦🇷',
    country: 'Argentina',
    quote: 'The checklists helped me standardize the structure of my consultations and rely less on memory.',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/9f1829aa-db07-4a67-b49a-ba3f698dda8c.jpg',
  },
  {
    name: 'Carolina S.',
    flag: '🇨🇱',
    country: 'Chile',
    quote: "The Decision Map lets me quickly see what information I have, what's missing, and what I need to verify.",
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/e7be1cbc-941a-4d06-b4a3-509ee3562eb2.jpg',
  },
]

export const faqs = [
  {
    q: 'How will I receive the material?',
    a: "Immediately after payment, you'll receive an email with access to download all 6 volumes and 5 bonuses in digital format.",
  },
  {
    q: 'Is the content applicable worldwide?',
    a: 'The system is designed as an educational and organizational tool. Clinical decisions, prescribing information, contraindications, dosing, and other treatment-related matters should always be verified against current guidelines, official drug information, and applicable regulations in your jurisdiction.',
  },
  {
    q: 'Do I need to be a specialist?',
    a: 'No. The system is designed for healthcare professionals who work with or are interested in working with patients receiving GLP-1 therapies. Each professional remains responsible for applying appropriate clinical judgment and verifying information relevant to their practice.',
  },
  {
    q: 'Are the files downloadable?',
    a: "Yes. You'll receive digital files that can be accessed from a computer, tablet, or compatible device.",
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. You pay $27 once and receive lifetime access to the purchased materials. Currency conversion is automatic, so you pay the equivalent in your local currency.',
  },
  {
    q: 'Does this replace clinical guidelines?',
    a: 'No. GLP-1 Decision Map™ is an educational and organizational tool. It does not replace official guidelines, prescribing information, professional clinical judgment, or applicable regulations.',
  },
  {
    q: "What if I don't like it?",
    a: "You're protected by our 7-day guarantee. If you decide the product isn't right for you, you can request a refund within the applicable guarantee period.",
  },
]

export const purchaseNotifications = [
  { name: 'Valentina G.', place: 'Bogotá, Colombia' },
  { name: 'Ricardo M.', place: 'Guadalajara, Mexico' },
  { name: 'Lucía F.', place: 'Madrid, Spain' },
  { name: 'Diego A.', place: 'Buenos Aires, Argentina' },
  { name: 'Camila R.', place: 'Santiago, Chile' },
  { name: 'Javier S.', place: 'Lima, Peru' },
  { name: 'Mariana T.', place: 'Montevideo, Uruguay' },
  { name: 'Andrés P.', place: 'Quito, Ecuador' },
  { name: 'Gabriela N.', place: 'San José, Costa Rica' },
  { name: 'Fernando C.', place: 'Asunción, Paraguay' },
  { name: 'Paola V.', place: 'Ciudad de Panamá, Panama' },
  { name: 'Sofía D.', place: 'Barcelona, Spain' },
]
